/**
 * Fund Detail Page — Tabbed View
 *
 * Tabs: Overview | Investments | ESG | Compliance | Reports
 * Fund header with name, code, SFDR badge, status + transition buttons
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Edit2,
  Landmark,
  Globe,
  Target,
  Calendar,
  DollarSign,
  X,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { apiClient } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';
import type {
  Fund,
  FundCreatePayload,
  SFDRClassification,
  FundStatus,
  Investment,
  ESGSubmission,
  ComplianceFiling,
} from '@/types';

type TabKey = 'overview' | 'investments' | 'esg' | 'compliance' | 'reports';

const SFDR_LABELS: Record<string, string> = {
  article_6: 'Article 6',
  article_8: 'Article 8',
  article_9: 'Article 9',
};

const SFDR_VARIANTS: Record<string, 'default' | 'warning' | 'success'> = {
  article_6: 'default',
  article_8: 'warning',
  article_9: 'success',
};

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'info' | 'muted' | 'default'> = {
  active: 'success',
  fundraising: 'info',
  investing: 'success',
  monitoring: 'warning',
  exiting: 'warning',
  harvesting: 'warning',
  closed: 'muted',
};

const FUND_STATUS_TRANSITIONS: Record<string, FundStatus[]> = {
  active: ['fundraising', 'investing', 'monitoring', 'exiting', 'closed'],
  fundraising: ['investing', 'closed'],
  investing: ['monitoring', 'closed'],
  monitoring: ['exiting', 'closed'],
  exiting: ['closed'],
  closed: [],
};

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'investments', label: 'Investments' },
  { key: 'esg', label: 'ESG' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'reports', label: 'Reports' },
];

export function FundDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingTransition, setPendingTransition] = useState<FundStatus | null>(null);

  const fundQuery = useQuery<Fund>({
    queryKey: ['ic', 'fund', id],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/funds/${id}`);
      return res.data?.data ?? res.data;
    },
    enabled: !!id,
  });

  const investmentsQuery = useQuery<Investment[]>({
    queryKey: ['ic', 'fund', id, 'investments'],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/investments/`, { params: { fund_id: id } });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: !!id && activeTab === 'investments',
  });

  const esgQuery = useQuery<ESGSubmission[]>({
    queryKey: ['ic', 'fund', id, 'esg'],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/esg/submissions/`, { params: { fund_id: id } });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: !!id && activeTab === 'esg',
  });

  const filingsQuery = useQuery<ComplianceFiling[]>({
    queryKey: ['ic', 'fund', id, 'filings'],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/compliance/filings/`, { params: { fund_id: id } });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: !!id && activeTab === 'compliance',
  });

  const statusTransitionMutation = useMutation({
    mutationFn: async (newStatus: FundStatus) => {
      const res = await apiClient.put(`/api/v1/impact-core/funds/${id}`, { status: newStatus });
      return res.data;
    },
    onSuccess: () => {
      setPendingTransition(null);
      void queryClient.invalidateQueries({ queryKey: ['ic', 'fund', id] });
      void queryClient.invalidateQueries({ queryKey: ['ic', 'funds'] });
    },
  });

  const fund = fundQuery.data;

  if (fundQuery.isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-slate-700/50" />
        <div className="h-32 animate-pulse rounded-lg bg-slate-700/30" />
        <div className="h-64 animate-pulse rounded-lg bg-slate-700/30" />
      </div>
    );
  }

  if (fundQuery.isError || !fund) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-red-400">Failed to load fund details.</p>
          <button
            onClick={() => navigate('/funds')}
            className="mt-2 text-xs text-ic-accent hover:underline"
          >
            Back to Funds
          </button>
        </div>
      </div>
    );
  }

  const sfdr = fund.sfdr_category ?? fund.sfdr_classification;

  return (
    <div className="space-y-6">
      {/* Back + Header */}
      <div>
        <button
          onClick={() => navigate('/funds')}
          className="mb-3 flex items-center gap-1.5 text-xs text-ic-text-tertiary hover:text-ic-text-secondary transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Funds
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-semibold text-ic-text-primary">{fund.name}</h1>
              <StatusBadge
                label={SFDR_LABELS[sfdr] ?? sfdr}
                variant={SFDR_VARIANTS[sfdr] ?? 'default'}
                size="md"
              />
              <StatusBadge
                label={fund.status}
                variant={STATUS_VARIANTS[fund.status] ?? 'default'}
                size="md"
              />
            </div>
            {fund.fund_code && (
              <p className="mt-1 font-mono text-xs text-ic-text-tertiary">{fund.fund_code}</p>
            )}
          </div>

          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-1.5 rounded-md border border-ic-border px-3 py-2 text-xs font-medium text-ic-text-secondary hover:border-ic-accent/50 hover:text-ic-accent transition-colors"
          >
            <Edit2 className="h-3.5 w-3.5" />
            Edit
          </button>
        </div>
      </div>

      {/* Status Lifecycle Section */}
      {(() => {
        const transitions = FUND_STATUS_TRANSITIONS[fund.status] ?? [];
        if (transitions.length === 0 && !pendingTransition) return null;
        return (
          <div className="rounded-lg border border-ic-border bg-ic-card p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
                  Status Lifecycle
                </span>
                <StatusBadge
                  label={fund.status}
                  variant={STATUS_VARIANTS[fund.status] ?? 'default'}
                  size="md"
                />
              </div>
              {transitions.length > 0 && !pendingTransition && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-ic-text-tertiary mr-1">Transition to:</span>
                  {transitions.map((next) => (
                    <button
                      key={next}
                      onClick={() => setPendingTransition(next)}
                      className="rounded-md border border-ic-border px-2.5 py-1 text-[10px] font-medium text-ic-text-secondary hover:border-ic-accent/50 hover:text-ic-accent transition-colors capitalize"
                    >
                      {next.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              )}
              {pendingTransition && (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-ic-text-secondary">
                    Change status from <strong className="text-ic-text-primary capitalize">{fund.status}</strong> to{' '}
                    <strong className="text-ic-accent capitalize">{pendingTransition.replace('_', ' ')}</strong>?
                  </span>
                  <button
                    onClick={() => statusTransitionMutation.mutate(pendingTransition)}
                    disabled={statusTransitionMutation.isPending}
                    className="rounded-md bg-ic-accent px-3 py-1 text-[10px] font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
                  >
                    {statusTransitionMutation.isPending ? 'Updating...' : 'Confirm'}
                  </button>
                  <button
                    onClick={() => setPendingTransition(null)}
                    className="rounded-md border border-ic-border px-3 py-1 text-[10px] font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Tabs */}
      <div className="border-b border-ic-border">
        <div className="flex gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-ic-accent text-ic-accent'
                  : 'border-transparent text-ic-text-tertiary hover:text-ic-text-secondary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab fund={fund} />}
      {activeTab === 'investments' && (
        <DataTable
          loading={investmentsQuery.isLoading}
          error={investmentsQuery.isError}
          data={investmentsQuery.data ?? []}
          emptyMessage="No investments for this fund yet."
          columns={['Investee', 'Amount', 'Currency', 'Status', 'Date']}
          renderRow={(inv: Investment) => (
            <tr
              key={inv.id}
              className="border-b border-ic-border/50 last:border-0 cursor-pointer hover:bg-ic-hover/50"
              onClick={() => navigate(`/investments/${inv.id}`)}
            >
              <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{inv.investee_name}</td>
              <td className="py-2.5 px-4 font-mono text-xs text-ic-text-secondary">
                ${inv.amount.toLocaleString()}
              </td>
              <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{inv.currency}</td>
              <td className="py-2.5 px-4">
                <StatusBadge label={inv.status} variant={inv.status === 'active' ? 'success' : 'default'} />
              </td>
              <td className="py-2.5 px-4 text-xs text-ic-text-tertiary">
                {inv.date ? format(parseISO(inv.date), 'dd MMM yyyy') : '--'}
              </td>
            </tr>
          )}
        />
      )}
      {activeTab === 'esg' && (
        <DataTable
          loading={esgQuery.isLoading}
          error={esgQuery.isError}
          data={esgQuery.data ?? []}
          emptyMessage="No ESG submissions for this fund yet."
          columns={['Investee', 'Period', 'Status', 'Completion', 'Submitted']}
          renderRow={(sub: ESGSubmission) => (
            <tr key={sub.id} className="border-b border-ic-border/50 last:border-0">
              <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{sub.investee_name}</td>
              <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{sub.reporting_period}</td>
              <td className="py-2.5 px-4">
                <StatusBadge
                  label={sub.status}
                  variant={sub.status === 'approved' ? 'success' : sub.status === 'rejected' ? 'danger' : 'default'}
                />
              </td>
              <td className="py-2.5 px-4 font-mono text-xs text-ic-text-secondary">{sub.completion_percent}%</td>
              <td className="py-2.5 px-4 text-xs text-ic-text-tertiary">
                {sub.submitted_at ? format(parseISO(sub.submitted_at), 'dd MMM yyyy') : '--'}
              </td>
            </tr>
          )}
        />
      )}
      {activeTab === 'compliance' && (
        <DataTable
          loading={filingsQuery.isLoading}
          error={filingsQuery.isError}
          data={filingsQuery.data ?? []}
          emptyMessage="No compliance filings for this fund yet."
          columns={['Type', 'Period', 'Status', 'Due Date', 'Submitted']}
          renderRow={(filing: ComplianceFiling) => (
            <tr key={filing.id} className="border-b border-ic-border/50 last:border-0">
              <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{filing.filing_type}</td>
              <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{filing.period}</td>
              <td className="py-2.5 px-4">
                <StatusBadge
                  label={filing.status}
                  variant={filing.status === 'approved' ? 'success' : filing.status === 'rejected' ? 'danger' : 'default'}
                />
              </td>
              <td className="py-2.5 px-4 text-xs text-ic-text-tertiary">
                {filing.due_date ? format(parseISO(filing.due_date), 'dd MMM yyyy') : '--'}
              </td>
              <td className="py-2.5 px-4 text-xs text-ic-text-tertiary">
                {filing.submitted_date ? format(parseISO(filing.submitted_date), 'dd MMM yyyy') : '--'}
              </td>
            </tr>
          )}
        />
      )}
      {activeTab === 'reports' && (
        <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
          <p className="text-xs text-ic-text-tertiary">Fund reports will be listed here. Use the Reports page to generate.</p>
        </div>
      )}

      {/* Edit Fund Modal */}
      {showEditModal && fund && (
        <FundEditModal
          fund={fund}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            setShowEditModal(false);
            void queryClient.invalidateQueries({ queryKey: ['ic', 'fund', id] });
            void queryClient.invalidateQueries({ queryKey: ['ic', 'funds'] });
          }}
        />
      )}
    </div>
  );
}

// ─── Fund Edit Modal ───────────────────────────────────────────

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'JPY', 'ZAR', 'KES', 'NGN'];

function FundEditModal({ fund, onClose, onSaved }: { fund: Fund; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(fund.name);
  const [fundCode, setFundCode] = useState(fund.fund_code ?? '');
  const [sfdrCategory, setSfdrCategory] = useState<SFDRClassification>(fund.sfdr_category ?? fund.sfdr_classification);
  const [status, setStatus] = useState<FundStatus>(fund.status);
  const [currency, setCurrency] = useState(fund.base_currency ?? fund.currency);
  const [targetSize, setTargetSize] = useState((fund.target_size ?? fund.fund_size ?? '').toString());
  const [vintageYear, setVintageYear] = useState((fund.vintage_year ?? '').toString());
  const [geoTags, setGeoTags] = useState<string[]>(fund.geography_focus ?? []);
  const [geoInput, setGeoInput] = useState('');
  const [sectorTags, setSectorTags] = useState<string[]>(fund.sector_focus ?? []);
  const [sectorInput, setSectorInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateMutation = useMutation({
    mutationFn: async (payload: FundCreatePayload) => {
      const res = await apiClient.put(`/api/v1/impact-core/funds/${fund.id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      onSaved();
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string | Array<{ msg: string; loc: string[] }> } } };
      const detail = axiosErr.response?.data?.detail;
      if (typeof detail === 'string') {
        setErrors({ _general: detail });
      } else if (Array.isArray(detail)) {
        const mapped: Record<string, string> = {};
        for (const item of detail) {
          const field = item.loc[item.loc.length - 1] ?? '_general';
          mapped[field] = item.msg;
        }
        setErrors(mapped);
      } else {
        setErrors({ _general: 'Failed to update fund. Please try again.' });
      }
    },
  });

  function handleAddTag(
    value: string,
    tags: string[],
    setTags: (t: string[]) => void,
    setInput: (v: string) => void,
  ) {
    const trimmed = value.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setInput('');
  }

  function handleRemoveTag(tag: string, tags: string[], setTags: (t: string[]) => void) {
    setTags(tags.filter((t) => t !== tag));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    if (!name.trim()) {
      setErrors({ name: 'Fund name is required' });
      return;
    }

    const payload: FundCreatePayload = {
      name: name.trim(),
      fund_code: fundCode.trim() || undefined,
      sfdr_category: sfdrCategory,
      status,
      base_currency: currency,
      target_size: targetSize ? Number(targetSize) : undefined,
      vintage_year: vintageYear ? Number(vintageYear) : undefined,
      geography_focus: geoTags.length > 0 ? geoTags : undefined,
      sector_focus: sectorTags.length > 0 ? sectorTags : undefined,
    };

    updateMutation.mutate(payload);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <h2 className="text-sm font-semibold text-ic-text-primary">Edit Fund</h2>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4 max-h-[70vh] overflow-y-auto">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
              {errors._general}
            </div>
          )}

          {/* Name */}
          <EditFieldGroup label="Fund Name *" error={errors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
            />
          </EditFieldGroup>

          {/* Code + SFDR */}
          <div className="grid grid-cols-2 gap-4">
            <EditFieldGroup label="Fund Code" error={errors.fund_code}>
              <input
                type="text"
                value={fundCode}
                onChange={(e) => setFundCode(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
              />
            </EditFieldGroup>
            <EditFieldGroup label="SFDR Category" error={errors.sfdr_category}>
              <select
                value={sfdrCategory}
                onChange={(e) => setSfdrCategory(e.target.value as SFDRClassification)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="article_6">Article 6</option>
                <option value="article_8">Article 8</option>
                <option value="article_9">Article 9</option>
              </select>
            </EditFieldGroup>
          </div>

          {/* Status + Currency */}
          <div className="grid grid-cols-2 gap-4">
            <EditFieldGroup label="Status" error={errors.status}>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as FundStatus)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="active">Active</option>
                <option value="fundraising">Fundraising</option>
                <option value="investing">Investing</option>
                <option value="monitoring">Monitoring</option>
                <option value="exiting">Exiting</option>
                <option value="closed">Closed</option>
              </select>
            </EditFieldGroup>
            <EditFieldGroup label="Base Currency" error={errors.base_currency}>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </EditFieldGroup>
          </div>

          {/* Target Size + Vintage */}
          <div className="grid grid-cols-2 gap-4">
            <EditFieldGroup label="Target Size (USD)" error={errors.target_size}>
              <input
                type="number"
                value={targetSize}
                onChange={(e) => setTargetSize(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
              />
            </EditFieldGroup>
            <EditFieldGroup label="Vintage Year" error={errors.vintage_year}>
              <input
                type="number"
                value={vintageYear}
                onChange={(e) => setVintageYear(e.target.value)}
                min="2000"
                max="2100"
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none"
              />
            </EditFieldGroup>
          </div>

          {/* Geography Focus (tags) */}
          <EditFieldGroup label="Geography Focus" error={errors.geography_focus}>
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-ic-border bg-ic-input px-2 py-1.5 min-h-[36px]">
              {geoTags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 rounded bg-ic-accent/15 px-2 py-0.5 text-[10px] font-medium text-ic-accent">
                  {tag}
                  <button type="button" onClick={() => handleRemoveTag(tag, geoTags, setGeoTags)} className="hover:text-white">
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={geoInput}
                onChange={(e) => setGeoInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    handleAddTag(geoInput, geoTags, setGeoTags, setGeoInput);
                  }
                }}
                placeholder={geoTags.length === 0 ? 'Type and press Enter...' : ''}
                className="flex-1 min-w-[80px] bg-transparent text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:outline-none"
              />
            </div>
          </EditFieldGroup>

          {/* Sector Focus (tags) */}
          <EditFieldGroup label="Sector Focus" error={errors.sector_focus}>
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-ic-border bg-ic-input px-2 py-1.5 min-h-[36px]">
              {sectorTags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 rounded bg-ic-accent/15 px-2 py-0.5 text-[10px] font-medium text-ic-accent">
                  {tag}
                  <button type="button" onClick={() => handleRemoveTag(tag, sectorTags, setSectorTags)} className="hover:text-white">
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={sectorInput}
                onChange={(e) => setSectorInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    handleAddTag(sectorInput, sectorTags, setSectorTags, setSectorInput);
                  }
                }}
                placeholder={sectorTags.length === 0 ? 'Type and press Enter...' : ''}
                className="flex-1 min-w-[80px] bg-transparent text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:outline-none"
              />
            </div>
          </EditFieldGroup>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditFieldGroup({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
        {label}
      </label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}

// ─── Sub-Components ────────────────────────────────────────────

function OverviewTab({ fund }: { fund: Fund }) {
  const sfdr = fund.sfdr_category ?? fund.sfdr_classification;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Fund Details Card */}
      <div className="rounded-lg border border-ic-border bg-ic-card p-4 space-y-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">Fund Details</h3>

        <div className="grid grid-cols-2 gap-3">
          <MetaField icon={<Landmark className="h-3.5 w-3.5" />} label="SFDR" value={SFDR_LABELS[sfdr] ?? sfdr} />
          <MetaField icon={<DollarSign className="h-3.5 w-3.5" />} label="Currency" value={fund.base_currency ?? fund.currency} />
          <MetaField icon={<Target className="h-3.5 w-3.5" />} label="Target Size" value={formatSize(fund.target_size ?? fund.fund_size)} />
          <MetaField icon={<Calendar className="h-3.5 w-3.5" />} label="Vintage" value={fund.vintage_year?.toString() ?? '--'} />
        </div>
      </div>

      {/* Focus Areas */}
      <div className="rounded-lg border border-ic-border bg-ic-card p-4 space-y-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">Focus Areas</h3>

        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-1.5 mb-2 text-ic-text-tertiary">
              <Globe className="h-3.5 w-3.5" />
              <span className="text-[10px] uppercase tracking-wider font-medium">Geography</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(fund.geography_focus ?? []).length > 0 ? (
                fund.geography_focus?.map((g) => (
                  <span key={g} className="rounded bg-blue-500/10 px-2 py-0.5 text-[10px] text-blue-400">{g}</span>
                ))
              ) : (
                <span className="text-xs text-ic-text-tertiary">Not specified</span>
              )}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-2 text-ic-text-tertiary">
              <Target className="h-3.5 w-3.5" />
              <span className="text-[10px] uppercase tracking-wider font-medium">Sectors</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(fund.sector_focus ?? []).length > 0 ? (
                fund.sector_focus?.map((s) => (
                  <span key={s} className="rounded bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-400">{s}</span>
                ))
              ) : (
                <span className="text-xs text-ic-text-tertiary">Not specified</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Timestamps */}
      <div className="lg:col-span-2 rounded-lg border border-ic-border bg-ic-card p-4">
        <div className="flex gap-8">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-ic-text-tertiary">Created</span>
            <p className="text-xs text-ic-text-secondary">
              {fund.created_at ? format(parseISO(fund.created_at), 'dd MMMM yyyy, HH:mm') : '--'}
            </p>
          </div>
          {fund.updated_at && (
            <div>
              <span className="text-[10px] uppercase tracking-wider text-ic-text-tertiary">Last Updated</span>
              <p className="text-xs text-ic-text-secondary">
                {format(parseISO(fund.updated_at), 'dd MMMM yyyy, HH:mm')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetaField({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-ic-text-tertiary">{icon}</span>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-ic-text-tertiary">{label}</p>
        <p className="text-xs font-medium text-ic-text-primary">{value}</p>
      </div>
    </div>
  );
}

function formatSize(amount: number | undefined): string {
  if (!amount) return '--';
  if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`;
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`;
  return `$${amount.toLocaleString()}`;
}

interface DataTableProps<T> {
  loading: boolean;
  error: boolean;
  data: T[];
  emptyMessage: string;
  columns: string[];
  renderRow: (item: T) => React.ReactNode;
}

function DataTable<T>({ loading, error, data, emptyMessage, columns, renderRow }: DataTableProps<T>) {
  if (loading) {
    return (
      <div className="rounded-lg border border-ic-border bg-ic-card p-4 space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 animate-pulse rounded bg-slate-700/40" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-32 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
        <p className="text-xs text-red-400">Failed to load data.</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
        <p className="text-xs text-ic-text-tertiary">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
      <table className="w-full text-xs ic-table">
        <thead>
          <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
            {columns.map((col) => (
              <th key={col} className="px-4 pb-2 pt-3 font-medium">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item) => renderRow(item))}
        </tbody>
      </table>
    </div>
  );
}
